import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Link,
  Image,
  Container,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { SessionContext } from "./SessionContext";
import { supabase } from "./supabaseClient";

export default function Auth() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [resetStep, setResetStep] = useState(0); // 0=login, 1=enter email, 2=enter OTP+password
  const [resetEmail, setResetEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const navigate = useNavigate();
  const { login, setIsRecoveryFlow } = useContext(SessionContext);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const result = await login(email, password);
    if (result.success) {
      navigate("/home");
    } else {
      setMessage(result.error);
    }
    setLoading(false);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail);
    if (error) {
      setMessage(error.message);
    } else {
      setMessage("Doğrulama kodu gönderildi. E-postanızı kontrol edin.");
      setResetStep(2);
    }
    setLoading(false);
  };

  const handleVerifyAndUpdate = async (e) => {
    e.preventDefault();
    setMessage("");
    if (newPassword.length < 6) {
      setMessage("Şifre en az 6 karakter olmalıdır.");
      return;
    }
    setLoading(true);
    setIsRecoveryFlow(true);

    const { error: verifyError } = await supabase.auth.verifyOtp({
      email: resetEmail,
      token: otpCode,
      type: "recovery",
    });
    if (verifyError) {
      setMessage("Kod geçersiz veya süresi dolmuş. Tekrar deneyin.");
      setIsRecoveryFlow(false);
      setLoading(false);
      return;
    }

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (updateError) {
      setMessage("Şifre güncellenirken hata oluştu. Tekrar deneyin.");
      setIsRecoveryFlow(false);
    } else {
      setMessage("Şifre başarıyla güncellendi!");
      setIsRecoveryFlow(false);
      await supabase.auth.signOut();
      setOtpCode("");
      setNewPassword("");
      setResetEmail("");
      setTimeout(() => {
        setResetStep(0);
        setMessage("");
      }, 2000);
    }
    setLoading(false);
  };

  // Step 1: Enter email to receive OTP
  if (resetStep === 1) {
    return (
      <Container maxW="md" display="flex" h="100vh" w="100vw" justifyContent="center" alignContent="center">
        <Box display="flex" flexDirection="column" justifyContent="center" w="100%">
          <Box margin="auto" p={2} w="100%">
            <Box backgroundColor="gray.500" p={2} my={5} borderRadius="md" justifyContent="center" display="flex">
              <Image src="/logo-1.svg" />
            </Box>
            <form onSubmit={handleSendCode}>
              <VStack spacing={4}>
                <Text fontSize="xl" fontWeight="bold">Şifre Sıfırlama</Text>
                <FormControl>
                  <FormLabel w="100%" textAlign="center">Email</FormLabel>
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </FormControl>
                <Button type="submit" colorScheme="blue" isLoading={loading}>
                  Kod Gönder
                </Button>
                <Button variant="ghost" onClick={() => { setResetStep(0); setMessage(""); }}>
                  Giriş Ekranına Dön
                </Button>
                {message && (
                  <Text color={message.includes("gönderildi") ? "green.500" : "red.500"}>
                    {message}
                  </Text>
                )}
              </VStack>
            </form>
          </Box>
        </Box>
      </Container>
    );
  }

  // Step 2: Enter OTP code + new password
  if (resetStep === 2) {
    return (
      <Container maxW="md" display="flex" h="100vh" w="100vw" justifyContent="center" alignContent="center">
        <Box display="flex" flexDirection="column" justifyContent="center" w="100%">
          <Box margin="auto" p={2} w="100%">
            <Box backgroundColor="gray.500" p={2} my={5} borderRadius="md" justifyContent="center" display="flex">
              <Image src="/logo-1.svg" />
            </Box>
            <form onSubmit={handleVerifyAndUpdate} autoComplete="off">
              <VStack spacing={4}>
                <Text fontSize="xl" fontWeight="bold">Yeni Şifre Belirle</Text>
                <FormControl>
                  <FormLabel w="100%" textAlign="center">Email</FormLabel>
                  <Input
                    type="email"
                    value={resetEmail}
                    isReadOnly
                    bg="gray.100"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel w="100%" textAlign="center">8 Haneli Doğrulama Kodu</FormLabel>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                    autoComplete="one-time-code"
                    name="otp-code"
                    id="otp-code"
                    required
                  />
                </FormControl>
                <FormControl>
                  <FormLabel w="100%" textAlign="center">Yeni Şifre</FormLabel>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    name="new-password"
                    id="new-password"
                    required
                  />
                </FormControl>
                <Button type="submit" colorScheme="blue" isLoading={loading}>
                  Şifreyi Güncelle
                </Button>
                <Button variant="ghost" onClick={() => { setResetStep(1); setMessage(""); setOtpCode(""); setNewPassword(""); }}>
                  Geri Dön
                </Button>
                {message && (
                  <Text color={message.includes("başarıyla") ? "green.500" : "red.500"}>
                    {message}
                  </Text>
                )}
              </VStack>
            </form>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxW="md"
      justifyContent="center"
      alignContent="center"
      display="flex"
      h="100vh"
      w="100vw"
    >
      <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} w={"100%"}>
        <Box margin="auto" p={2} alignItems={"center" }  w={"100%"}>
          <Box
            backgroundColor={"gray.500"}
            p={2}
            my={5}
            borderRadius={"md"}
            justifyContent={"center"}
            display={"flex"}
          >
            <Link to="/">
              <Image
                src={"/logo-1.svg"}
              />
            </Link>
          </Box>

          <form onSubmit={handleSignIn}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel w={"100%"} textAlign={"center"}>
                  Email
                </FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormControl>
              <FormControl>
                <FormLabel w={"100%"} textAlign={"center"}>
                  Password
                </FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormControl>
              <Button type="submit" colorScheme="blue" isLoading={loading}>
                Giriş Yap
              </Button>
              <Link onClick={() => { setResetStep(1); setMessage(""); }}>
                Şifremi Unuttum?
              </Link>
              {message && <Text color="red.500">{message}</Text>}
            </VStack>
          </form>
        </Box>
      </Box>
    </Container>
  );
}
